import config from "utils/config";
import {
  ApolloClient,
  createHttpLink,
  InMemoryCache,
  NormalizedCacheObject,
  ApolloLink,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { cache as reactCache } from "react";

const httpLink = createHttpLink({
  uri: config.get("GRAPHQL_URL"),
  // KHÔNG dùng credentials "include": CMS trả Access-Control-Allow-Origin "*",
  // trình duyệt chặn CORS mọi request có cookie → search/ward client-side chết.
  // Auth đi bằng header Bearer (authLink), không cần cookie. (Bản main cũ đặt
  // "include" ở ApolloClient() nên bị bỏ qua — vì vậy trước giờ vẫn chạy.)
  credentials: "same-origin",
});

const authLink = setContext((_, { headers }) => {
  const token = config.get("token");
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    },
  };
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach((error) => {
      console.error(`GraphQL error:`, error);
    });
  }

  if (networkError) {
    console.error(`Network error: ${networkError}`);
  }
});

export const createApolloClient = (): ApolloClient<NormalizedCacheObject> => {
  try {
    return new ApolloClient({
      link: ApolloLink.from([errorLink, authLink, httpLink]),
      cache: new InMemoryCache(),
      // Web không dùng normalized cache (không useQuery/readQuery); caching HTTP đã có
      // revalidate/ISR của Next lo → mặc định bỏ cache Apollo để shop vừa sửa giá/ảnh,
      // ưu đãi vừa hết hạn, sitemap… không bị trả dữ liệu cũ. Service nào cần vẫn truyền
      // fetchPolicy riêng.
      defaultOptions: {
        query: { fetchPolicy: "no-cache" },
        watchQuery: { fetchPolicy: "no-cache" },
        mutate: { fetchPolicy: "no-cache" },
      },
    });
  } catch (error) {
    console.error("Error creating Apollo client:", error);
    throw error;
  }
};

/**
 * Lấy Apollo client đúng phạm vi:
 * - Server (RSC / route handler / sitemap): client MỚI cho mỗi request — bọc React cache()
 *   nên các Service.find trong cùng 1 lượt render dùng chung 1 instance, nhưng KHÔNG chia sẻ
 *   state (InMemoryCache, header) giữa người dùng khác nhau. Ngoài phạm vi render (sitemap,
 *   route handler) cache() không ghi nhớ → mỗi lần gọi là 1 client mới, vẫn đúng.
 * - Browser: singleton (1 phiên = 1 người dùng, cần giữ token đăng nhập qua authLink).
 */
const getServerClient = reactCache(() => createApolloClient());
let browserClient: ApolloClient<NormalizedCacheObject> | null = null;

export const getClient = (): ApolloClient<NormalizedCacheObject> => {
  if (typeof window === "undefined") return getServerClient();
  if (!browserClient) browserClient = createApolloClient();
  return browserClient;
};

export { default as builder } from "./builder";
